var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Divider, Grid, Stack, Typography, useMediaQuery, useTheme } from "@mui/material";
import { grey } from "@mui/material/colors";
import { MacroNutrientText } from "./MacroNutrient-types";
import { getUnitDisplay, hasValidNutrientValue } from "./MacroNutrient-utils";
/**
 * A component that displays macro nutrient information.
 *
 * @param {MacroNutrientProps} nutrients
 * @returns {JSX.Element | null} Returns the nutrient display component or null if no nutrients are provided.
 */
export const MacroNutrients = ({ nutrients }) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t;
    if (!nutrients || !hasValidNutrientValue(nutrients))
        return null;
    // If the nutrient quantity is not defined we set it to 0 so it can be filtered out.
    // Otherwise it results in the type being number | undefined.
    const nutrientData = [
        {
            type: MacroNutrientText.CAL,
            value: (_b = (_a = nutrients.calories) === null || _a === void 0 ? void 0 : _a.quantity) !== null && _b !== void 0 ? _b : 0,
            unit: getUnitDisplay((_c = nutrients.calories) === null || _c === void 0 ? void 0 : _c.unit),
        },
        { type: MacroNutrientText.FAT, value: (_e = (_d = nutrients.fat) === null || _d === void 0 ? void 0 : _d.quantity) !== null && _e !== void 0 ? _e : 0, unit: getUnitDisplay((_f = nutrients.fat) === null || _f === void 0 ? void 0 : _f.unit) },
        {
            type: MacroNutrientText.CARBS,
            value: (_h = (_g = nutrients.carbohydrates) === null || _g === void 0 ? void 0 : _g.quantity) !== null && _h !== void 0 ? _h : 0,
            unit: getUnitDisplay((_j = nutrients.carbohydrates) === null || _j === void 0 ? void 0 : _j.unit),
        },
        {
            type: MacroNutrientText.FIBER,
            value: (_l = (_k = nutrients.fiber) === null || _k === void 0 ? void 0 : _k.quantity) !== null && _l !== void 0 ? _l : 0,
            unit: getUnitDisplay((_m = nutrients.fiber) === null || _m === void 0 ? void 0 : _m.unit),
        },
        {
            type: MacroNutrientText.SUGAR,
            value: (_p = (_o = nutrients.sugar) === null || _o === void 0 ? void 0 : _o.quantity) !== null && _p !== void 0 ? _p : 0,
            unit: getUnitDisplay((_q = nutrients.sugar) === null || _q === void 0 ? void 0 : _q.unit),
        },
        {
            type: MacroNutrientText.PROTEIN,
            value: (_s = (_r = nutrients.protein) === null || _r === void 0 ? void 0 : _r.quantity) !== null && _s !== void 0 ? _s : 0,
            unit: getUnitDisplay((_t = nutrients.protein) === null || _t === void 0 ? void 0 : _t.unit),
        },
    ];
    const filteredNutrients = nutrientData.filter((n) => n.value > 0);
    return (_jsxs(Grid, Object.assign({ container: true, direction: "row", alignItems: "center" }, { children: [filteredNutrients.map(({ type, value, unit }) => (_jsx(Grid, Object.assign({ item: true, xs: "auto" }, { children: _jsx(NutrientDisplay, { type: type, value: value, unit: unit }) }), type))), _jsx(Grid, Object.assign({ item: true, xs: "auto" }, { children: _jsx(PerServingText, {}) }))] })));
};
/**
 * A component that displays a single nutrient and a vertical divider.
 *
 * @param {Object} props - The properties for the component.
 * @param {string} props.type - The type of the macro nutrient (e.g., "Calories", "Fat").
 * @param {string} props.value - The value of the macro nutrient (e.g., "200").
 * @param {string} props.unit - The unit of the macro nutrient (e.g., "g").
 * @returns {JSX.Element} - Returns a JSX element displaying the nutrient and a divider.
 */
const NutrientDisplay = (_a) => {
    var { children } = _a, rest = __rest(_a, ["children"]);
    return (_jsxs(Stack, Object.assign({ direction: "row", justifyContent: "space-around" }, { children: [_jsx(MacroNutrient, Object.assign({}, rest)), _jsx(Divider, { sx: { mx: 0.5, my: 0 }, color: "primary", orientation: "vertical", variant: "middle", flexItem: true })] })));
};
/**
 * A component that displays the nutrient type and its value.
 *
 * @param {Object} props - The properties for the component.
 * @param {string} props.type - The type of the macro nutrient (e.g., "Calories", "Fat").
 * @param {string} props.value - The value of the macro nutrient (e.g., "200g").
 * @returns {JSX.Element} - Returns a JSX element displaying the nutrient type and value.
 */
export const MacroNutrient = ({ type, value, unit }) => {
    return (
    // minWidth is set to maintain alignment of each nutrient
    _jsxs(Stack, Object.assign({ alignItems: "center", sx: { minWidth: "40px" } }, { children: [_jsx(Typography, Object.assign({ sx: {
                    color: grey[700],
                    fontSize: ".625rem",
                } }, { children: type })), _jsx(Typography, Object.assign({ variant: "caption", sx: {
                    fontWeight: "bold",
                } }, { children: `${value}${unit}` }))] })));
};
const PerServingText = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"), { noSsr: true });
    const [firstPart, secondPart] = MacroNutrientText.PER_SERVING.split(" ");
    if (isMobile) {
        return (_jsxs(Stack, { children: [_jsx(Typography, Object.assign({ variant: "caption" }, { children: firstPart })), _jsx(Typography, Object.assign({ variant: "caption" }, { children: secondPart }))] }));
    }
    return (_jsx(Typography, Object.assign({ variant: "caption" }, { children: MacroNutrientText.PER_SERVING })));
};
